import { ref } from '@vue/composition-api'
import { Centrifuge, UnauthorizedError } from 'centrifuge'

import AuthService from '@/core/auth/AuthService'
import { notificationService } from '@/utils/notifications'
import { $themeConfig } from '@themeConfig'

let centrifugeInstance = null
const subscriptions = new Map()
const subscriptionHandlers = new Map()
const isConnected = ref(false)

const getConnectionToken = async () => {
    if (
        !AuthService.isInitialized ||
        !AuthService.keycloak?.authenticated ||
        !AuthService.keycloak?.token
    ) {
        throw new UnauthorizedError('User is not authenticated')
    }

    await AuthService.updateToken(30)

    const { token } = AuthService.keycloak
    if (!token) {
        throw new UnauthorizedError('Keycloak access token is unavailable')
    }

    return token
}

export function useCentrifugo() {
    const connect = () => {
        if (centrifugeInstance && centrifugeInstance.state === 'connected') {
            isConnected.value = true
            return
        }

        if (!centrifugeInstance) {
            const url = $themeConfig.app?.centrifugoWsUrl
            if (typeof url !== 'string' || !/^wss?:\/\//.test(url)) {
                throw new Error('VITE_APP_CENTRIFUGO_URL is not configured')
            }

            centrifugeInstance = new Centrifuge(url, {
                debug: import.meta.env.DEV,
                getToken: getConnectionToken,
            })

            centrifugeInstance.on('connected', () => {
                isConnected.value = true
            })

            centrifugeInstance.on('disconnected', () => {
                isConnected.value = false
            })

            centrifugeInstance.on('error', ctx => {
                console.error('Centrifugo error', ctx)
            })
        }

        centrifugeInstance.connect()
    }

    const ready = async (timeout = 5000) => {
        connect()
        return centrifugeInstance.ready(timeout)
    }

    const rpc = async (method, data) => {
        if (typeof method !== 'string' || !method) {
            throw new TypeError(
                'Centrifugo RPC method must be a non-empty string'
            )
        }

        connect()
        return centrifugeInstance.rpc(method, data)
    }

    const disconnect = () => {
        if (centrifugeInstance) {
            subscriptions.forEach((sub, channel) => {
                try {
                    sub.unsubscribe()
                    sub.removeAllListeners()
                    centrifugeInstance.removeSubscription(sub)
                } catch (e) {
                    console.error(
                        `[WS: Centrifugo] Ошибка отписки при disconnect от ${channel}:`,
                        e
                    )
                }
            })
            subscriptions.clear()
            subscriptionHandlers.clear()

            centrifugeInstance.disconnect()
            centrifugeInstance = null
        }
        isConnected.value = false
    }

    /**
     * Отписаться от канала
     * @param {string} channel
     * @param {function} [handler]
     * @returns {boolean} — true, если отписка прошла успешно
     */
    const unsubscribe = (channel, handler) => {
        const sub = subscriptions.get(channel)
        if (!sub) return false

        const handlers = subscriptionHandlers.get(channel)
        if (handler && handlers) {
            handlers.delete(handler)
            if (handlers.size > 0) return true
        }

        try {
            sub.unsubscribe()
            sub.removeAllListeners()
            centrifugeInstance?.removeSubscription(sub)
        } catch (err) {
            console.error(
                `[WS: Centrifugo] Ошибка при ручной отписке от канала ${channel}:`,
                err
            )
        } finally {
            subscriptions.delete(channel)
            subscriptionHandlers.delete(channel)
        }
        return true
    }

    /**
     * Подписывается на канал и передает входящие сообщения в колбэк.
     * @param {string} channel - Имя канала для подписки.
     * @param {function} onPublication - Функция-обработчик, которая будет вызвана с данными (data) при получении сообщения.
     * @returns {object|null} - Возвращает объект подписки для возможности отписаться, или null в случае ошибки.
     */
    const subscribe = (channel, onPublication = null) => {
        if (!centrifugeInstance) {
            console.error(
                '[WS: Centrifugo] Centrifugo не подключен. Невозможно подписаться на канал:',
                channel
            )
            return null
        }

        if (!subscriptionHandlers.has(channel)) {
            subscriptionHandlers.set(channel, new Set())
        }
        if (typeof onPublication === 'function') {
            subscriptionHandlers.get(channel).add(onPublication)
        }

        // Одна подписка на канал раздаёт публикации всем зарегистрированным обработчикам
        if (subscriptions.has(channel)) {
            return subscriptions.get(channel)
        }

        const sub =
            centrifugeInstance.getSubscription(channel) ||
            centrifugeInstance.newSubscription(channel)

        subscriptions.set(channel, sub)
        sub.on('publication', ctx => {
            subscriptionHandlers
                .get(channel)
                ?.forEach(handler => handler(ctx.data))
        })

        sub.on('error', ctx => {
            console.error(`[WS: Centrifugo] Ошибка подписки на канал ${channel}:`, ctx)
            notificationService.showError(
                ctx.error,
                'Ошибка получения уведомлений',
                { timeout: false }
            )
        })

        sub.on('unsubscribed', ctx => {
            if (ctx.code === 0) return

            const subscriptionError = {
                code: ctx.code,
                message: ctx.reason || 'Subscription error',
            }
            console.error(
                `[WS: Centrifugo] Подписка на канал ${channel} остановлена:`,
                ctx
            )
            notificationService.showError(
                subscriptionError,
                'Ошибка получения уведомлений',
                { timeout: false }
            )
        })

        sub.subscribe()
        return sub
    }

    return {
        connect,
        disconnect,
        ready,
        subscribe,
        unsubscribe,
        rpc,
        isConnected,
    }
}
